import { db, storage } from '../config/firebase';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    DocumentData,
    QueryDocumentSnapshot
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { CoffeeBean } from '../types/coffee';

const COLLECTION_NAME = 'coffees';

// Helper function to retry operations with exponential backoff
const retryOperation = async (operation: () => Promise<any>, maxRetries = 3, baseDelay = 1000) => {
    let lastError;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error) {
            console.error(`Operation failed (attempt ${attempt + 1}/${maxRetries}):`, error);
            lastError = error;
            // Exponential backoff with jitter
            const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
    throw lastError;
};

// Helper function to check if URL is a Firebase Storage URL
const isFirebaseStorageUrl = (url: string): boolean => {
    try {
        return url.includes('firebasestorage.googleapis.com') || 
               url.includes('storage.googleapis.com') ||
               url.startsWith('gs://');
    } catch {
        return false;
    }
};

export const coffeeService = {
    // Thêm cà phê mới với ảnh
    async addCoffee(coffee: CoffeeBean, imageFiles: File[]) {
        try {
            // Upload nhiều ảnh với retry
            const imageUrls = await Promise.all(
                imageFiles.map(async (file) => {
                    return retryOperation(async () => {
                        const storageRef = ref(storage, `coffee-images/${Date.now()}_${file.name}`);
                        const uploadResult = await uploadBytes(storageRef, file);
                        return getDownloadURL(uploadResult.ref);
                    });
                })
            );

            // Thêm document vào Firestore với retry
            const docRef = await retryOperation(async () => {
                return addDoc(collection(db, COLLECTION_NAME), {
                    ...coffee,
                    images: imageUrls,
                    createdAt: new Date(),
                    updatedAt: new Date()
                });
            });

            return { id: docRef.id, ...coffee, images: imageUrls };
        } catch (error) {
            throw new Error('Không thể thêm cà phê: ' + error);
        }
    },

    // Lấy tất cả cà phê
    async getAllCoffees() {
        try {
            const querySnapshot = await retryOperation(async () => {
                return getDocs(
                    query(
                        collection(db, COLLECTION_NAME),
                        // Sắp xếp theo trường createdAt giảm dần (mới nhất lên đầu)
                        orderBy('createdAt', 'desc')
                    )
                );
            });
            
            return querySnapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
                id: doc.id,
                ...doc.data()
            })) as CoffeeBean[];
        } catch (error) {
            throw new Error('Không thể lấy danh sách cà phê: ' + error);
        }
    },

    // Lấy cà phê theo ID
    async getCoffeeById(id: string) {
        try {
            const docSnap = await retryOperation(async () => {
                const docRef = doc(db, COLLECTION_NAME, id);
                return getDoc(docRef);
            });

            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() } as CoffeeBean;
            }
            return null;
        } catch (error) {
            throw new Error('Không thể lấy thông tin cà phê: ' + error);
        }
    },

    // Tìm kiếm cà phê theo mã
    async getCoffeeByCode(code: string) {
        try {
            const querySnapshot = await retryOperation(async () => {
                const q = query(
                    collection(db, COLLECTION_NAME),
                    where('codeCoffee', '==', code)
                );
                return getDocs(q);
            });
            
            if (!querySnapshot.empty) {
                const doc = querySnapshot.docs[0];
                return { id: doc.id, ...doc.data() } as CoffeeBean;
            }
            return null;
        } catch (error) {
            throw new Error('Không thể tìm cà phê theo mã: ' + error);
        }
    },

    // Cập nhật thông tin cà phê
    async updateCoffee(id: string, coffee: Partial<CoffeeBean>, newImageFiles?: File[], oldImageUrls?: string[]) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const currentDoc = await retryOperation(async () => getDoc(docRef));

            if (!currentDoc.exists()) {
                throw new Error('Không tìm thấy cà phê');
            }

            let updateData = {
                ...coffee,
                updatedAt: new Date()
            };

            // Xử lý ảnh cũ
            if (oldImageUrls && oldImageUrls.length > 0) {
                const currentImageUrls = currentDoc.data().images || [];
                const imagesToDelete = currentImageUrls.filter((url: string) => !oldImageUrls.includes(url));
                
                // Xóa từng ảnh cũ riêng biệt với retry
                let deletedOldCount = 0;
                let skippedOldCount = 0;
                
                for (const url of imagesToDelete) {
                    // Chỉ xóa nếu là Firebase Storage URL
                    if (isFirebaseStorageUrl(url)) {
                        try {
                            await retryOperation(async () => {
                                const imageRef = ref(storage, url);
                                return deleteObject(imageRef);
                            });
                            deletedOldCount++;
                            console.log(`Successfully deleted old image: ${url}`);
                        } catch (error) {
                            console.error(`Error deleting old image ${url}:`, error);
                            // Tiếp tục xử lý dù có lỗi khi xóa ảnh
                        }
                    } else {
                        skippedOldCount++;
                        console.log(`Skipped non-Firebase Storage URL: ${url}`);
                    }
                }

                if (imagesToDelete.length > 0) {
                    console.log(`Update cleanup for coffee ID ${id}: ${deletedOldCount} old images deleted, ${skippedOldCount} non-Firebase URLs skipped`);
                }

                updateData = {
                    ...updateData,
                    images: oldImageUrls
                };
            }

            // Upload ảnh mới với retry và chunking
            if (newImageFiles?.length) {
                const chunkSize = 3; // Xử lý tối đa 3 ảnh một lần
                let newImageUrls: string[] = [];
                
                // Xử lý upload theo từng nhóm nhỏ
                for (let i = 0; i < newImageFiles.length; i += chunkSize) {
                    const chunk = newImageFiles.slice(i, i + chunkSize);
                    const chunkUrls = await Promise.all(
                        chunk.map(async (file) => {
                            return retryOperation(async () => {
                                const storageRef = ref(storage, `coffee-images/${Date.now()}_${file.name}`);
                                const uploadResult = await uploadBytes(storageRef, file);
                                return getDownloadURL(uploadResult.ref);
                            });
                        })
                    );
                    newImageUrls = [...newImageUrls, ...chunkUrls];
                }

                updateData = {
                    ...updateData,
                    images: [...(updateData.images || []), ...newImageUrls]
                };
            }

            // Cập nhật Firestore với retry
            await retryOperation(async () => updateDoc(docRef, updateData));
            
            return { id, ...updateData };
        } catch (error) {
            throw new Error('Không thể cập nhật cà phê: ' + error);
        }
    },

    // Xóa cà phê
    async deleteCoffee(id: string) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const docSnap = await retryOperation(async () => getDoc(docRef));

            if (docSnap.exists()) {
                const data = docSnap.data();
                const imageUrls = data.images || [];
                
                // Xóa tất cả ảnh từ Firebase Storage
                let deletedCount = 0;
                let skippedCount = 0;
                
                for (const url of imageUrls) {
                    // Chỉ xóa nếu là Firebase Storage URL
                    if (isFirebaseStorageUrl(url)) {
                        try {
                            await retryOperation(async () => {
                                const imageRef = ref(storage, url);
                                return deleteObject(imageRef);
                            });
                            deletedCount++;
                            console.log(`Successfully deleted image: ${url}`);
                        } catch (error) {
                            console.error(`Error deleting image ${url}:`, error);
                            // Tiếp tục xử lý dù có lỗi khi xóa ảnh
                        }
                    } else {
                        skippedCount++;
                        console.log(`Skipped non-Firebase Storage URL: ${url}`);
                    }
                }

                // Ghi log để theo dõi số lượng ảnh đã xóa
                console.log(`Storage cleanup for coffee ID ${id}: ${deletedCount} images deleted, ${skippedCount} non-Firebase URLs skipped`);
                
                // Note: driveImages không cần xóa khỏi Firebase Storage vì chúng được lưu trên Google Drive
                // Chỉ cần xóa metadata trong Firestore
                const driveImages = data.driveImages || [];
                if (driveImages.length > 0) {
                    console.log(`Coffee had ${driveImages.length} Google Drive images (metadata only, no storage cleanup needed)`);
                }
            }

            // Xóa document từ Firestore
            await retryOperation(async () => deleteDoc(docRef));
            console.log(`Successfully deleted coffee document with ID: ${id}`);
        } catch (error) {
            console.error(`Error deleting coffee with ID ${id}:`, error);
            throw new Error('Không thể xóa cà phê: ' + error);
        }
    },

    // Tìm kiếm cà phê theo region
    async searchByRegion(region: string) {
        try {
            const querySnapshot = await retryOperation(async () => {
                const q = query(
                    collection(db, COLLECTION_NAME),
                    where('region', 'array-contains', region)
                );
                return getDocs(q);
            });
            
            return querySnapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
                id: doc.id,
                ...doc.data()
            })) as CoffeeBean[];
        } catch (error) {
            throw new Error('Không thể tìm kiếm cà phê theo vùng: ' + error);
        }
    },

    // Utility function để dọn dẹp ảnh không còn sử dụng
    async cleanupOrphanedImages() {
        try {
            console.log('Starting orphaned images cleanup...');
            
            // Lấy tất cả URL ảnh đang được sử dụng
            const querySnapshot = await retryOperation(async () => {
                return getDocs(collection(db, COLLECTION_NAME));
            });
            
            const usedImageUrls = new Set<string>();
            querySnapshot.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
                const data = doc.data();
                const images = data.images || [];
                images.forEach((url: string) => {
                    if (isFirebaseStorageUrl(url)) {
                        usedImageUrls.add(url);
                    }
                });
            });

            console.log(`Found ${usedImageUrls.size} images currently in use`);
            
            // Note: Để dọn dẹp hoàn toàn, cần list tất cả files trong Storage và so sánh
            // Điều này cần quyền admin và có thể tốn kém, nên chỉ log thông tin hiện tại
            
            return {
                usedImagesCount: usedImageUrls.size,
                message: 'Cleanup scan completed. Use Firebase Console for detailed storage cleanup.'
            };
        } catch (error) {
            console.error('Error during cleanup:', error);
            throw new Error('Không thể dọn dẹp ảnh: ' + error);
        }
    }
}; 